import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {catchError, filter, first, map, shareReplay, switchMap, takeUntil, tap} from 'rxjs/operators';
import {Planning, PLANNING_METADATA} from 'smag-commons/dto/msg';
import {BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {I18NService} from '../../i18n/i18n.service';
import {MsgBoxService} from '../../shared/msg-box.service';
import {SmagApiService} from '../../api/smag-api.service';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {APP_CONFIG_TOKEN, AppConfig} from '../../app.config';
import {ECMode} from 'smag-commons/cfg/hierarchy';
import {EnergyCommunityNetwork} from '../../shared/entities';
import {isDefined} from '../../commons/commons';
import {Permission} from 'smag-commons/dto/api';
import {can} from '../../shared/permissions';
import {ECNetwork, ECSelection} from '../../shared/ec-resource-tree/ec-resource-tree.component';
import {NetworkService} from '../../shared/network.service';
import {PLANNER_NAMES} from '../../shared/plannings';
import {SharedEnergyData} from '../../shared/shared-energy-chart/shared-energy-chart.component';
import {mkHourlyBins} from './ec-planning-util';
import {mkMode} from '../../shared/energy-community';

interface Network {
    root: HierarchyEntity;
}

interface PlanningResult {
    sharedPower: number;
    injectedPower: number;
    sharingRatio: number;
}

@Component({
    selector: 'app-ec-planning',
    templateUrl: './ec-planning.component.html',
    styleUrls: ['./ec-planning.component.scss']
})
export class ECPlanningComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();

    contentResize: Observable<void>;
    planning: Observable<Planning>;
    network: Observable<Network>;
    planningResult: Observable<PlanningResult>;
    treeNetwork: ECNetwork; // network for tree
    resourceId: Subject<string>;
    ecMode: ECMode;

    isDefined = isDefined;
    ECMode = ECMode;

    constructor(private readonly router: Router,
                private readonly route: ActivatedRoute,
                @Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly title: Title,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly apiService: SmagApiService,
                private readonly networkService: NetworkService,
                private readonly hierarchyService: HierarchyService) {
        this.planning = null;
        this.network = null;
        this.ecMode = null;
        this.treeNetwork = null;
        this.resourceId = new BehaviorSubject(null);
    }

    ngOnInit() {
        this.planning = this.route.paramMap.pipe(
            map(params => params.get('planningId')),
            tap(planningId => {
                this.title.setTitle(this.i18n.translate('Planning'));
            }),
            switchMap(planningId => this.loadPlanning(planningId).pipe(
                catchError(err => {
                    this.msgBoxService.error(this.i18n.translate('An error occurred while loading planning {0}', planningId), err);
                    return EMPTY;
                }))),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.network = this.planning.pipe(
            switchMap(planning => !!planning ?
                forkJoin([
                    this.hierarchyService.findEntityById(planning.network_id).pipe(first()),
                    this.apiService.getNetworkOfResource(planning.network_id)
                ]).pipe(
                    map(([networkEntity, networkInfo]) => ({
                        root: networkEntity
                    } as Network))
                ) :
                of(null)),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.planning.pipe(takeUntil(this.destroyed)).subscribe(p => {
            this.networkService.buildECNetwork(p.network_id).toPromise()
                .then(entityNetwork => {
                    this.treeNetwork = {
                        network: entityNetwork as EnergyCommunityNetwork,
                        selectedResourceId: null
                    };
                    this.resourceId.next(p.network_id);
                });
        });
        combineLatest([this.planning, this.hierarchyService.selection]).pipe(
            filter(([planning, entity]) => !entity || planning.network_id !== entity.id),
            map(([planning, entity]) => entity ? entity.id : null),
            takeUntil(this.destroyed)
        ).subscribe(id => {
            this.router.navigate(['/c', 'optimization'], {
                queryParams: {
                    id: id || undefined
                },
                queryParamsHandling: 'merge'
            });
        });
        this.network.pipe(
            filter(network => !!network && can(network.root.permissions, Permission.READ)),
            takeUntil(this.destroyed)
        ).subscribe(e => {
            this.loadECMode(e.root.id);
        });
        this.planningResult = this.planning.pipe(map(planning => {
            const sharedEnergyData = this.mkSharedEnergyData(planning);
            const sharedPower = sharedEnergyData ?
                sharedEnergyData.reduce((accumulator, currentValue) => accumulator + currentValue.sharedEnergy, 0) : 0;
            const injectedPower = sharedEnergyData ?
                sharedEnergyData.reduce((accumulator, currentValue) => accumulator + currentValue.injectedEnergy, 0) : 0;
            const sharingRatio = injectedPower > 0 ? Math.round(sharedPower / injectedPower * 100) : 0;
            return {injectedPower, sharedPower, sharingRatio} as PlanningResult;
        }), takeUntil(this.destroyed));
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    private loadECMode(networkId: string) {
        this.apiService.communityPodsStatistics(networkId).pipe(
            map(stats => mkMode(stats))
        ).subscribe(ecMode => {
            this.ecMode = ecMode;
        });
    }

    /**
     * @deprecated
     * @param planning
     * @private
     */
    private setupPlanning(planning?: Planning): Observable<EnergyCommunityNetwork> {
        if (planning) {
            return this.networkService.buildECNetwork(planning.network_id).pipe(
                map(network => network as EnergyCommunityNetwork),
                first()
            );
        } else {
            return of(null);
        }
    }

    onClickGotoResources(entity: HierarchyEntity) {
        if (entity) {
            this.router.navigate(['/c', 'ec', 'resources'], {
                queryParams: {
                    id: entity.id
                },
                queryParamsHandling: 'merge'
            }).then();
        }
    }

    private loadPlanning(planningId: string): Observable<Planning> {
        return this.apiService.retrievePlanning(planningId, true);
    }

    onSelectResource(event: ECSelection) {
        this.resourceId.next(event.resourceId);
    }

    plannerName(planning: Planning): string {
        const plannerId = !!planning.metadata
        && !!planning.metadata[PLANNING_METADATA.PLANNER_KEY]
            ? planning.metadata[PLANNING_METADATA.PLANNER_KEY] : null;
        return PLANNER_NAMES[plannerId] || plannerId;
    }

    private mkSharedEnergyData(planning: Planning): SharedEnergyData[] {
        const data = planning.data ? planning.data[planning.network_id] || {} : {};
        return mkHourlyBins(planning.timeframe, data, planning.end_time).map(bin => ({
            time: bin.baseTimestamp,
            absorbedEnergy: bin.absorbedEnergy,
            injectedEnergy: bin.injectedEnergy,
            sharedEnergy: bin.sharedEnergy,
            opacity: 1
        }));
    }
}
